import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  AlertController,
  IonicModule,
  IonInput,
  LoadingController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { Editor, Extension } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import CodeBlock from '@tiptap/extension-code-block';
import Header from '@tiptap/extension-heading';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import StarterKit from '@tiptap/starter-kit';
import _ from 'lodash';
import Markdown from 'markdown-it';
import { NgxTippyModule, NgxTippyService } from 'ngx-tippy-wrapper';

import {
  CONVERSATION_STATUS,
  MESSAGE_SOURCE_TYPE,
  PRIVATE_CHANNEL_NAME,
  SUPPORT_STEP_ASSIGNEE_TYPES,
  UNTHREAD_SEND_EXTERNAL_BCC,
  UNTHREAD_SEND_EXTERNAL_CC,
  UNTHREAD_SEND_EXTERNAL_CC_REMOVE,
  UNTHREAD_SEND_EXTERNAL_PREFIX,
  UNTHREAD_SEND_EXTERNAL_SEND,
} from 'src/app/constants';
import { CustomEmoji } from 'src/app/utils/tip-tap-extensions/custom-emoji';
import { attachEventListenerToElement, removeUndefinedFields, validateEmail } from 'src/app/utils/utils';
import {
  Automation,
  Conversation,
  KnowledgeBaseArticle,
  Profile,
  Recipient,
  RunQueryResult,
  SlackChannel,
  Template,
  WhereClause,
} from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomersService } from 'src/services/customers.service';
import { EmojisService } from 'src/services/emojis.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackUserGroupsService } from 'src/services/slack-user-groups.service';
import { SlackService } from 'src/services/slack.service';
import { UsersService } from 'src/services/users.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { AvatarModule } from '../avatar/avatar.module';
import { EmojiPickerComponent } from '../emoji-picker/emoji-picker.component';
import { GiphyModalComponent } from '../giphy-modal/giphy-modal.component';
import { ImageModalComponent } from '../image-modal/image-modal.component';
import { InlineFileAttachmentComponent } from '../inline-file-attachment/inline-file-attachment.component';
import { Block, BLOCK_TYPES, renderBlocks } from '../slack-blocks/common';
import { SlackBlockPreviewComponent } from '../slack-blocks/slack-block-preview/slack-block-preview.component';
import { SlackMarkdown } from '../slack-blocks/slack-markdown';
import { SlackMessageComponent } from '../slack-message/slack-message.component';
import { SnoozeComponent } from '../snooze/snooze.component';
import { TextPopoverComponent } from '../text-popover/text-popover.component';
import { TriageComponent } from '../triage/triage.component';
import { TriggerManualAutomationModalComponent } from '../trigger-manual-automation-modal/trigger-manual-automation-modal.component';

function formatUserName(user: Partial<Profile>, connectedTeams: any[]) {
  return `${user.name}${connectedTeams?.length > 1 && user.slackTeam?.name ? ` (${user.slackTeam.name})` : ''}`;
}

enum SLASH_COMMAND_TYPE {
  ARTICLE = 'article',
  ATTACHMENT = 'attachment',
  GIF = 'gif',
  AI_RESPONSE = 'ai-response',
  AUTOMATION = 'automation',
  TEMPLATE = 'template',
}

// Custom Mention extension that can parse existing mention HTML
const CustomMention = Mention.extend({
  addAttributes() {
    // Override the default addAttributes to prevent TipTap from reading data-id directly
    // We want our parseHTML to handle the ID construction
    return {
      id: {
        default: null,
        // Don't parse from HTML - let our custom parseHTML handle it
        parseHTML: () => null,
        // Don't render to HTML - let our custom renderHTML handle it
        renderHTML: () => ({}),
      },
      label: {
        default: null,
        parseHTML: () => null,
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span[data-type="user-mention"]',
        getAttrs: (element: HTMLElement) => {
          const slackId = element.getAttribute('data-slack-id');
          const unthreadId = element.getAttribute('data-id');
          const textContent = element.textContent || '';
          const label = textContent.replace('@', '');

          // Allow parsing even without unthreadId (backend doesn't always provide it)
          // At least one of slackId or unthreadId should exist for a valid mention
          if (!slackId && !unthreadId) {
            return false;
          }

          return {
            id: `${unthreadId ?? ''}|${slackId ?? ''}|user`,
            label: label || slackId || '',
          };
        },
      },
      {
        tag: 'span[data-type="slack-usergroup-mention"]',
        getAttrs: (element: HTMLElement) => {
          const slackId = element.getAttribute('data-slack-id');
          const textContent = element.textContent || '';
          const label = textContent.replace('@', '');

          if (!slackId) {
            return false;
          }

          // Usergroups only have Slack IDs, no Unthread IDs
          return {
            id: `|${slackId}|team`,
            label: label,
          };
        },
      },
      {
        // Parse channel mentions
        tag: 'span[data-type="slack-channel-mention"]',
        getAttrs: (element: HTMLElement) => {
          const channelId = element.getAttribute('data-slack-id') || element.getAttribute('data-id');
          const textContent = element.textContent || '';
          const label = textContent.replace('#', '');

          if (!channelId) {
            return false;
          }

          // Channels only have Slack IDs, no Unthread IDs
          return {
            id: `|${channelId}|channel`,
            label: label,
          };
        },
      },
      {
        // Also parse the new format we're generating
        tag: 'span[data-type="mention"]',
        getAttrs: (element: HTMLElement) => {
          const id = element.getAttribute('data-id');
          const label = element.getAttribute('data-label');

          if (!id || !label) return false;

          return {
            id: id,
            label: label,
          };
        },
      },
    ];
  },
});

@Component({
  selector: 'app-reply-composer',
  templateUrl: './reply-composer.component.html',
  styleUrls: ['./reply-composer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    RouterModule,
    NgxTippyModule,
    AvatarModule,
    SlackBlockPreviewComponent,
    InlineFileAttachmentComponent,
    SlackMessageComponent,
  ],
})
export class ReplyComposerComponent implements OnInit, OnChanges {
  @Input() conversation: Conversation;
  @Input() timelineEvents: any[];
  @Input() hasUserChatWriteScope = false;
  @Input() hasUserFilesWriteScope = false;
  @Input() hasTenantFilesWriteScope = false;
  @Input() repliesDisabled = false;
  @Input() connectUserToSlack: () => Promise<void>;

  // Inputs for inline editing
  @Input() isInlineEdit = false;
  @Input() initialContent = '';
  @Input() initialResolvedContent: any[] = []; // Add input for resolved content array
  @Input() showAttachments = true;
  @Input() showInternalNoteToggle = true;
  @Input() showSendButton = true;

  @HostBinding('class.inline-edit') get isInlineEditMode() {
    return this.isInlineEdit;
  }

  @Output() didSendPendingMessage = new EventEmitter<any>();
  @Output() didInsertKnowledgebaseArticle = new EventEmitter<Partial<KnowledgeBaseArticle>>();
  @Output() didRunAutomation = new EventEmitter<Partial<Automation>>();
  @Output() didSendTestEmail = new EventEmitter<any>();
  @Output() saveEditClicked = new EventEmitter<string>();
  @Output() cancelEditClicked = new EventEmitter<void>();
  @ViewChild('editorWrapper') editorWrapper: any;
  @ViewChild('emailRecipientsWrapper', { static: false, read: ElementRef })
  emailRecipientsWrapper: ElementRef;
  @ViewChild('ccInput', { static: false }) ccInput: IonInput;
  @ViewChild('bccInput') bccInput: IonInput;

  SLASH_COMMAND_OPTIONS = [
    {
      title: 'Knowledge base article',
      description: 'Insert link to an article',
      icon: 'book-outline',
      type: SLASH_COMMAND_TYPE.ARTICLE,
    },
    {
      title: 'Attachment',
      description: 'Insert an image or other file type',
      icon: 'image-outline',
      type: SLASH_COMMAND_TYPE.ATTACHMENT,
    },
    {
      title: 'Automation',
      description: 'Run an automation',
      icon: 'flash-outline',
      type: SLASH_COMMAND_TYPE.AUTOMATION,
    },
    {
      title: 'GIF',
      description: 'Insert a gif from a selection',
      icon: 'custom-gif',
      type: SLASH_COMMAND_TYPE.GIF,
    },
    {
      title: 'Template',
      description: 'Insert a custom pre-built message',
      icon: 'shapes-outline',
      type: SLASH_COMMAND_TYPE.TEMPLATE,
    },
  ];

  public editor: Editor;
  public isFocused = false;
  public replyingToThreadId: string;
  public replyingToThreadLabel: string;
  public messageBlocks: Block[];
  public templateHtml: string;
  public attachments: File[] = [];
  public tippyName = 'editor-tippy';
  public isDragOver = false;

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    // Only clear when leaving the container, not when moving over children
    const target = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as Node | null;
    if (!target || (related && target.contains(related))) {
      return;
    }
    this.isDragOver = false;
  }

  onDropClearDrag(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    // If the drop occurred inside the TipTap editor, let TipTap handle it
    const editorEl = document.querySelector('.editor') as HTMLElement | null;
    const dropTarget = event.target as Node | null;
    if (editorEl && dropTarget && editorEl.contains(dropTarget)) {
      return;
    }

    if (event.dataTransfer?.files?.length) {
      for (const file of Array.from(event.dataTransfer.files)) {
        this.analyticsService.trackEvent('Conversation Details', 'Drop File');
        this.attachFile(file);
      }
      return;
    }

    const uriList = event.dataTransfer?.getData('text/uri-list');
    const textPlain = event.dataTransfer?.getData('text/plain');
    const urlCandidate = uriList || textPlain;

    if (urlCandidate && (urlCandidate.startsWith('http') || urlCandidate.startsWith('data:image/'))) {
      const block = {
        type: 'image',
        image_url: urlCandidate,
        alt_text: '',
      } as any;

      if (this.messageBlocks) {
        this.messageBlocks.push(block);
      } else {
        this.messageBlocks = [block];
      }

      this.analyticsService.trackEvent('Conversation Details', 'Drop Image');
    }
  }

  connectedTeams: any[];

  isWidgetBased: boolean;
  isEmailBased: boolean;
  isDashboardBased: boolean;
  isMicrosoftTeamsBased: boolean;
  isBotDmBased: boolean;
  isSendingReply: boolean;
  isContentEmpty = true;
  isConnectingUserToSlack = false;
  isGeneratingAiResponse = false;

  isLoading = false;

  isShowingTo: boolean;
  isShowingCc: boolean;
  isShowingBcc: boolean;
  tos: Recipient[] = [];
  ccs: Recipient[] = [];
  bccs: Recipient[] = [];
  originalCcs: Recipient[] = [];
  ccText: string;
  bccText: string;
  isCcFocused: boolean;
  isBccFocused: boolean;

  public autocompleteSearchMode: 'users' | 'emojis' | 'slash' | 'articles' | 'automations' | 'channels' | 'none' =
    'none';
  public taggableUsers: Profile[];
  public taggableChannels: Partial<SlackChannel>[];
  public emojisMap: any;
  public emojisList: any[];
  public slashCommands = this.SLASH_COMMAND_OPTIONS;
  public articles: Partial<KnowledgeBaseArticle>[];
  public automations: Partial<Automation>[];
  highlightedItemIndex = -1;
  taggableRange: { from: number; to: number };
  keyCommandStartIndex: number;

  slackMarkdown = new SlackMarkdown();
  mentionListener: any;

  lastRequestId = 0;

  // Cache for channel members to prioritize them in user search
  // This cache is populated on first search and persists for the lifetime of the component
  private channelMembersCache: any[] = null;

  constructor(
    public authService: AuthService,
    private analyticsService: AnalyticsService,
    private searchablePopoverService: SearchablePopoverService,
    private msgSrvc: MessageService,
    private tippyService: NgxTippyService,
    private usersService: UsersService,
    private slackUserGroupsService: SlackUserGroupsService,
    private conversationsService: ConversationsService,
    private customersService: CustomersService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private slackService: SlackService,
    private apiService: ApiService,
    private emojisService: EmojisService,
    private automationsService: AutomationsService,
    private knowledgebaseArticlesService: KnowledgeBaseArticlesService,
  ) {
    if (authService.isAiFeatureEnabled('suggestResponses')) {
      this.SLASH_COMMAND_OPTIONS.unshift({
        title: 'AI-generated response',
        description: 'Insert a draft generated by AI',
        icon: 'sparkles-outline',
        type: SLASH_COMMAND_TYPE.AI_RESPONSE,
      });
      this.slashCommands = this.SLASH_COMMAND_OPTIONS;
    }
  }

  public focus(force: boolean) {
    if (this.isShowingBcc || this.isShowingCc) {
      return;
    }

    if (force || window.innerWidth >= 540) {
      this.editor.chain().focus();
    }
  }

  public blur() {
    this.editor.chain().blur();
  }

  async ngOnInit() {
    const randomString = Array(8)
      .fill(null)
      .map(() => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        return chars.charAt(Math.floor(Math.random() * chars.length));
      })
      .join('');
    this.tippyName = `editor-tippy-${randomString}`;

    const context = this;

    setTimeout(async () => {
      this.editor = new Editor({
        element: document.querySelector(`.editor`),
        injectCSS: true,
        editorProps: {
          handlePaste: (_props, event, slice) => {
            if (event.clipboardData?.files?.length) {
              for (const file of Array.from(event.clipboardData.files)) {
                this.analyticsService.trackEvent('Conversation Details', 'Paste Image');

                this.attachFile(file);
              }

              return true;
            }

            const imageUrl = (slice.content as any).content?.[0]?.attrs?.src;

            if (imageUrl) {
              const block = {
                type: 'image',
                image_url: imageUrl,
                alt_text: '',
              };

              if (this.messageBlocks) {
                this.messageBlocks.push(block);
              } else {
                this.messageBlocks = [block];
              }

              this.analyticsService.trackEvent('Conversation Details', 'Paste Image');

              return true;
            }
          },
          handleDrop: (_view: any, event: DragEvent, _slice: any, _moved: boolean) => {
            if (event.dataTransfer?.files?.length) {
              event.preventDefault();
              for (const file of Array.from(event.dataTransfer.files)) {
                this.analyticsService.trackEvent('Conversation Details', 'Drop File');
                this.attachFile(file);
              }
              return true;
            }

            const uriList = event.dataTransfer?.getData('text/uri-list');
            const textPlain = event.dataTransfer?.getData('text/plain');
            const urlCandidate = uriList || textPlain;

            if (urlCandidate && (urlCandidate.startsWith('http') || urlCandidate.startsWith('data:image/'))) {
              event.preventDefault();
              const block = {
                type: 'image',
                image_url: urlCandidate,
                alt_text: '',
              };

              if (this.messageBlocks) {
                this.messageBlocks.push(block);
              } else {
                this.messageBlocks = [block];
              }

              this.analyticsService.trackEvent('Conversation Details', 'Drop Image');
              return true;
            }

            return false;
          },
        },
        extensions: [
          StarterKit,
          CustomEmoji.configure({
            inline: true,
          }),
          Placeholder.configure({
            placeholder: 'Compose your message',
          }),
          BubbleMenu.configure({
            element: document.querySelector('.menu'),
          }),
          CustomMention.configure({
            HTMLAttributes: {
              class: 'mention-tag',
            },
            renderHTML({ options, node }) {
              const [unthreadId, slackId, type] = node.attrs.id.split('|');

              let dataType = 'user-mention';
              let prefix = options.suggestion.char;

              if (type === 'team') {
                dataType = 'slack-usergroup-mention';
              } else if (type === 'channel') {
                dataType = 'slack-channel-mention';
                prefix = '#';
              }

              const attrs = {
                'data-type': dataType,
                class: 'mention-tag',
              };

              if (slackId) {
                attrs['data-slack-id'] = slackId;
              }

              // Only add data-id if it exists (users have it, groups/channels don't)
              if (unthreadId) {
                attrs['data-id'] = unthreadId;
              }

              return ['span', attrs, `${prefix}${node.attrs.label ?? slackId ?? ''}`];
            },
            suggestion: {
              render: () => {
                return {
                  onStart: (props: any) => {
                    context.searchTaggableUsers('', true);
                    context.tippyService
                      .getInstance(context.tippyName)
                      .setProps({ getReferenceClientRect: props.clientRect });
                    context.tippyService.show(context.tippyName);
                    context.taggableRange = props.range;
                  },
                  onUpdate(props) {
                    context.searchTaggableUsers(props.query, true);
                    context.tippyService
                      .getInstance(context.tippyName)
                      .setProps({ getReferenceClientRect: props.clientRect });
                    context.tippyService.show(context.tippyName);
                    context.taggableRange = props.range;
                  },
                  onKeyDown(props) {
                    context.onKeyDown.apply(context, [props.event.key, 'editor']);
                    const specialKeys = ['Enter', 'Escape', 'ArrowDown', 'ArrowUp'];
                    if (specialKeys.includes(props.event.key)) {
                      return true;
                    }
                  },
                  onExit() {
                    context.hideAutocompleteTippy();
                  },
                };
              },
            },
          }),

          Link.configure({
            linkOnPaste: true,
          }),
          CodeBlock.configure({
            HTMLAttributes: {
              class: 'code',
            },
          }),
          Blockquote.configure({
            HTMLAttributes: {
              class: 'blockquote',
            },
          }),
          Image.configure({
            inline: true,
          }),
          Extension.create({
            addKeyboardShortcuts() {
              return {
                'Shift-Mod-Enter': () => {
                  if (context.isInlineEdit) {
                    context.emitSaveEdit();
                  } else {
                    context.sendReply(false, true);
                  }
                  return true;
                },
                'Mod-Enter': () => {
                  if (context.isInlineEdit) {
                    context.emitSaveEdit();
                  } else {
                    context.sendReply(false, false);
                  }
                  return true;
                },
                'Mod-k': () => {
                  context.addLink();
                  return true;
                },
                ':': (props) => {
                  const anchorIndex = props.editor.state.selection.anchor;
                  // Trigger only if : starts the message, or is preceded by a space
                  if (
                    anchorIndex === 1 ||
                    props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ' ' ||
                    props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ''
                  ) {
                    context.keyCommandStartIndex = anchorIndex;
                    context.searchEmojis('');
                  }
                  return false;
                },
                '/': (props) => {
                  // Disable slash commands in inline edit mode
                  if (context.isInlineEdit) {
                    return false;
                  }
                  const anchorIndex = props.editor.state.selection.anchor;
                  // Trigger only if : starts the message, or is preceded by a space
                  if (
                    anchorIndex === 1 ||
                    props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ' ' ||
                    props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ''
                  ) {
                    context.keyCommandStartIndex = anchorIndex;
                    context.searchSlashCommands('');
                  }
                  return false;
                },
                '#': (props) => {
                  const anchorIndex = props.editor.state.selection.anchor;
                  // Trigger only if # starts the message, or is preceded by a space
                  if (
                    anchorIndex === 1 ||
                    props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ' ' ||
                    props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ''
                  ) {
                    context.keyCommandStartIndex = anchorIndex;
                    context.searchChannels('');
                  }
                  return false;
                },
                ' ': (props) => {
                  const anchorIndex = props.editor.state.selection.anchor;
                  // Check if the character right before space is "/"
                  if (props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === '/') {
                    // If yes, hide autocomplete and reset
                    context.hideAutocompleteTippy();
                    context.autocompleteSearchMode = 'none';
                  }
                  return false;
                },
                ArrowDown: () => {
                  if (context.autocompleteSearchMode !== 'none') {
                    context.onKeyDown('ArrowDown', 'editor');
                    return true;
                  }
                  return false;
                },
                ArrowUp: () => {
                  if (context.autocompleteSearchMode !== 'none') {
                    context.onKeyDown('ArrowUp', 'editor');
                    return true;
                  }
                  return false;
                },
                Enter: () => {
                  if (context.autocompleteSearchMode !== 'none') {
                    context.onKeyDown('Enter', 'editor');
                    return true;
                  }
                  return false;
                },
                Escape: () => {
                  context.hideAutocompleteTippy();
                  return true;
                },
              };
            },
          }),
          Header.configure({
            levels: [1, 2, 3, 4, 5, 6],
          }),
          Strike,
          HorizontalRule,
        ],
        onFocus: () => {
          this.isFocused = true;

          if (!this.ccText?.length && !this.ccs?.length) {
            this.isShowingCc = false;
          }
          if (!this.bccText?.length && !this.bccs?.length) {
            this.isShowingBcc = false;
          }
        },
        onBlur: () => {
          this.isFocused = false;
          this.cacheDraftResponse();
        },
        onUpdate: ({ editor }) => {
          this.isContentEmpty = !editor.getText().length;

          if (
            this.autocompleteSearchMode === 'emojis' ||
            this.autocompleteSearchMode === 'slash' ||
            this.autocompleteSearchMode === 'articles' ||
            this.autocompleteSearchMode === 'channels' ||
            this.autocompleteSearchMode === 'automations'
          ) {
            if (this.keyCommandStartIndex <= this.editor.state.selection.anchor) {
              // increase start index by one to skip over the ':' or '/' character
              const search = (
                this.editor.state.doc.textBetween(this.keyCommandStartIndex + 1, this.editor.state.selection.anchor) ??
                ''
              ).toLowerCase();

              if (this.autocompleteSearchMode === 'emojis') {
                this.searchEmojis(search);
              } else if (this.autocompleteSearchMode === 'slash') {
                this.searchSlashCommands(search);
              } else if (this.autocompleteSearchMode === 'articles') {
                this.searchArticles(search);
              } else if (this.autocompleteSearchMode === 'channels') {
                this.searchChannels(search);
              } else if (this.autocompleteSearchMode === 'automations') {
                this.searchAutomations(search);
              }
            } else {
              this.hideAutocompleteTippy();
            }
          }
        },
      });

      if (this.isInlineEdit && this.initialContent) {
        // Use the initial HTML content which should have proper mention formatting
        this.editor.commands.setContent(this.initialContent);
      } else if (this.isInlineEdit && this.initialResolvedContent?.length) {
        // Fallback to resolved content if HTML isn't available
        const htmlContent = this.convertResolvedContentToHTML(this.initialResolvedContent);
        this.editor.commands.setContent(htmlContent);
      } else if (this.conversationsService.draftsCache[this.conversation.id]) {
        this.editor.commands.insertContent(this.conversationsService.draftsCache[this.conversation.id]);
      }

      this.mentionListener = attachEventListenerToElement('.editor', 'click', (e) => {
        if (e.target instanceof HTMLElement && e.target.tagName === 'A' && e.target.dataset.unthreadId) {
          e.preventDefault();
          e.stopPropagation();

          return false;
        }
      });
    }, 100);

    this.isEmailBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.EMAIL);
    this.isWidgetBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.WIDGET);
    this.isMicrosoftTeamsBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.TEAMS);
    this.isDashboardBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.DASHBOARD);
    this.isBotDmBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.SLACK_BOT_DM);

    this.connectedTeams = await this.slackService.getConnectedTeams();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['conversation'] && changes['conversation'].currentValue) {
      this.clearChannelMembersCache();
    }
  }

  getInternalTriageMessages() {
    return this.conversation.triageThreads?.filter((triage) => !triage.isCustomerFacing) ?? [];
  }

  onKeyDown(key: string, source: 'editor' | 'cc' | 'bcc') {
    const list =
      this.autocompleteSearchMode === 'emojis'
        ? this.emojisList
        : this.autocompleteSearchMode === 'slash'
          ? this.slashCommands
          : this.autocompleteSearchMode === 'users'
            ? this.taggableUsers
            : this.autocompleteSearchMode === 'channels'
              ? this.taggableChannels
              : this.autocompleteSearchMode === 'articles'
                ? this.articles
                : this.autocompleteSearchMode === 'automations'
                  ? this.automations
                  : [];

    if (key === 'Enter' || key === ',') {
      if (source === 'editor') {
        if (this.autocompleteSearchMode === 'users') {
          this.selectTaggedUser(this.taggableUsers[this.highlightedItemIndex]);
        } else if (this.autocompleteSearchMode === 'channels') {
          this.selectTaggedChannel(this.taggableChannels[this.highlightedItemIndex]);
        } else if (this.autocompleteSearchMode === 'emojis') {
          this.selectEmoji(this.emojisList[this.highlightedItemIndex]);
        } else if (this.autocompleteSearchMode === 'slash') {
          this.selectSlashCommand(this.slashCommands[this.highlightedItemIndex]);
        } else if (this.autocompleteSearchMode === 'articles') {
          this.selectArticle(this.articles[this.highlightedItemIndex]);
        } else if (this.autocompleteSearchMode === 'automations') {
          this.runAutomation(this.automations[this.highlightedItemIndex]);
        } else {
          this.hideAutocompleteTippy();
        }
      } else {
        const email =
          this.taggableUsers[this.highlightedItemIndex]?.email ??
          validateEmail(source === 'cc' ? this.ccText : this.bccText);

        this.selectUserByEmail(email, source);
      }
    } else if (key === 'Escape') {
      this.hideAutocompleteTippy();
    } else if (key === 'ArrowDown') {
      if (this.highlightedItemIndex < -1 || this.highlightedItemIndex > list.length) {
        this.highlightedItemIndex = -1;
      }

      this.highlightedItemIndex++;

      if (this.highlightedItemIndex >= list?.length) {
        this.highlightedItemIndex = 0;
      }
    } else if (key === 'ArrowUp') {
      this.highlightedItemIndex--;

      if (this.highlightedItemIndex < 0 && list?.length) {
        this.highlightedItemIndex = list.length - 1;
      }
    } else if (key === 'Backspace') {
      if (source === 'cc' && !this.ccText && this.ccs.length) {
        const currentCc = this.ccs[this.ccs.length - 1];
        if (currentCc.editable) {
          this.ccs.pop();
        }
      } else if (source === 'bcc' && !this.bccText && this.bccs.length) {
        const currentBcc = this.bccs[this.bccs.length - 1];
        if (currentBcc.editable) {
          this.bccs.pop();
        }
      }
    }
  }

  hideAutocompleteTippy() {
    this.autocompleteSearchMode = 'none';
    this.tippyService.hide(this.tippyName);
  }

  async cacheDraftResponse() {
    this.conversationsService.draftsCache[this.conversation.id] = this.editor.getHTML();
  }

  showTo() {
    this.isShowingTo = !this.isShowingTo;
  }

  toggleCcShowing() {
    this.isShowingCc = !this.isShowingCc;
    if (this.isShowingCc) {
      this.ccInput.setFocus();
    }
  }

  toggleBccShowing() {
    this.isShowingBcc = !this.isShowingBcc;
    if (this.isShowingBcc) {
      this.bccInput.setFocus();
    }
  }

  deleteCc(index: number) {
    this.ccs.splice(index, 1);
  }

  deleteBcc(index: number) {
    this.bccs.splice(index, 1);
  }

  async onCcChange() {
    if (this.ccText) {
      this.searchTaggableUsers(this.ccText, false);
      if (this.taggableUsers?.length) {
        this.tippyService.show(this.tippyName);
        this.tippyService.getInstance(this.tippyName).setProps({
          getReferenceClientRect: () => this.emailRecipientsWrapper.nativeElement.getBoundingClientRect(),
        });
      } else {
        this.hideAutocompleteTippy();
      }
    } else {
      this.hideAutocompleteTippy();
    }
  }

  async onBccChange() {
    if (this.bccText) {
      this.searchTaggableUsers(this.bccText, false);
      if (this.taggableUsers?.length) {
        this.tippyService.show(this.tippyName);
      } else {
        this.hideAutocompleteTippy();
      }
    } else {
      this.hideAutocompleteTippy();
    }
  }

  async onCcKeyDown($event) {
    this.onKeyDown($event.key, 'cc');
  }

  async onBccKeyDown($event) {
    this.onKeyDown($event.key, 'bcc');
  }

  onCcFocus() {
    this.isCcFocused = true;
  }

  onCcBlur() {
    setTimeout(() => {
      this.isCcFocused = false;
    }, 300);
  }

  onBccFocus() {
    this.isBccFocused = true;
  }

  onBccBlur() {
    setTimeout(() => {
      this.isBccFocused = false;
    }, 300);
  }

  async addLink() {
    const { view, state } = this.editor;
    const { from, to } = view.state.selection;
    const text = state.doc.textBetween(from, to, '');
    if (text?.startsWith('http')) {
      this.editor.commands.setLink({ href: text });
      return;
    }

    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        title: 'Add link',
        placeholder: 'https://',
        shouldSelect: true,
        showSaveButton: true,
        saveOnEnter: true,
        callback: async (href: string) => {
          if (href) {
            this.editor.commands.setLink({ href });
          }
        },
      },
      showBackdrop: true,
      event,
    });
    popover.present();
  }

  async removeStyling() {
    this.editor.commands.unsetAllMarks();
  }

  public insertTextContent(content: string) {
    this.editor.commands.insertContent(content, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
  }

  async insertTemplate() {
    this.searchablePopoverService.templates({
      event,
      side: 'top',
      templateType: 'slack',
      componentView: 'viewFromMessageInboxHeight',
      projectId: this.conversation.projectId,
      callback: async (_id: string, _title: string, template: Template) => {
        this.analyticsService.trackEvent('Conversation Details', 'Insert Template');

        const html: string = await this.apiService.postPromise(`/templates/${template.id}/render-html`, {
          customerId: this.conversation.customerId,
          conversationId: this.conversation.id,
          slackChannelId: this.conversation.channelId,
          slackTeamId: this.conversation.teamId,
        });

        // The template may have complicated elements that can't be easily edited as text in the box
        const editableBlockTypes = [BLOCK_TYPES.RICH_TEXT, BLOCK_TYPES.SECTION];

        if (template.chunks.some((chunk) => !editableBlockTypes.includes(chunk.type))) {
          this.messageBlocks = [html?.length ? html : renderBlocks(template.chunks)];
          this.templateHtml = html;
        } else {
          this.editor.commands.insertContent(html, {
            updateSelection: true,
            parseOptions: {
              preserveWhitespace: 'full',
            },
          });
        }
      },
    });
  }

  async insertEmoji() {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();

          if (emoji.native) {
            this.editor.commands.insertContent(emoji.native, {
              updateSelection: true,
              parseOptions: {
                preserveWhitespace: 'full',
              },
            });
          } else if (emoji.src) {
            this.editor.commands.insertCustomEmoji({
              name: emoji.name,
              src: emoji.src,
            });
          }

          this.analyticsService.trackEvent('Conversation Details', 'Insert Emoji');
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  async insertMention() {
    this.editor.commands.insertContent('@', {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
  }

  async insertChannelMention() {
    this.editor.commands.insertContent('#', {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
  }

  async attachFile(file: File) {
    if (this.hasTenantFilesWriteScope || this.hasUserFilesWriteScope) {
      this.attachments.push(file);
    }
  }

  async insertImage() {
    const modal = await this.modalCtrl.create({
      component: ImageModalComponent,
      componentProps: {
        searchValue: '',
        helperText: '',
      },
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();

    // if backdrop is clicked and no data passed
    if (!data || !data.uploadFile) {
      return;
    }

    try {
      await this.attachFile(data.uploadFile.file);
    } catch (err: any) {
      this.msgSrvc.show(err.message);
    }
  }

  async insertGif() {
    const modal = await this.modalCtrl.create({
      component: GiphyModalComponent,
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();
    if (data?.url) {
      this.messageBlocks = [
        {
          type: 'image',
          image_url: data.url,
          alt_text: '',
        },
      ];

      this.analyticsService.trackEvent('Conversation Details', 'Insert Gif');
    }
  }

  insertSlash() {
    this.editor.commands.insertContent('/', {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });

    this.keyCommandStartIndex = this.editor.state.selection.head - 1;
    this.searchSlashCommands('');
  }

  async searchTaggableUsers(searchText: string, includeTeams: boolean) {
    this.autocompleteSearchMode = 'users';

    const usersWhere: WhereClause[] = [
      {
        field: 'slackIds',
        operator: 'notIn',
        value: [null, []],
      },
      {
        field: 'status',
        operator: '==',
        value: 'active',
      },
      {
        field: 'name',
        operator: '!=',
        value: null,
      },
    ];

    const userTeamsWhere: WhereClause[] = [
      {
        field: 'name',
        operator: '!=',
        value: null,
      },
      {
        field: 'slackId',
        operator: '!=',
        value: null,
      },
    ];

    const slackTeamId = this.replyingToThreadId ? this.authService.tenant.slackTeamId : this.conversation.teamId;
    const slackTeam = slackTeamId ? await this.slackService.getTeam(slackTeamId) : undefined;

    if (slackTeamId) {
      userTeamsWhere.push({
        field: 'slackTeamId',
        operator: '==',
        value: slackTeamId,
      });
    }

    this.lastRequestId++;
    const requestId = this.lastRequestId;

    const data = [];

    // First, try to get channel members if we haven't cached them yet
    let channelMembers: any[] = [];

    if (!this.isBotDmBased && this.conversation.customerId && this.channelMembersCache === null) {
      try {
        const response = await this.customersService.getFollowers(this.conversation.customerId);
        const channelUsers = response.map((member) => member.user);

        this.channelMembersCache = channelUsers || [];
      } catch (error) {
        console.warn('Failed to fetch channel members:', error);
        this.channelMembersCache = [];
      }
    }

    // Filter channel members by search text if we have them
    if (this.channelMembersCache && this.channelMembersCache.length > 0) {
      channelMembers = this.channelMembersCache.filter((user) => {
        const userName = user?.name || '';
        const userEmail = user?.email || '';
        const searchLower = searchText.toLowerCase();

        return userName.toLowerCase().includes(searchLower) || userEmail.toLowerCase().includes(searchLower);
      });
    }

    // If we have channel members that match the search, use them
    if (channelMembers.length > 0) {
      // Add channel members first (prioritized)
      channelMembers.forEach((user) => {
        if (user) {
          data.push({
            name: formatUserName(user, this.connectedTeams),
            id: user.id,
            image: user.photo,
            email: user.email,
            type: SUPPORT_STEP_ASSIGNEE_TYPES.USER,
            object: user,
            slackId: user.slackId || (user as any).slackIds?.[0], // Try slackIds array if slackId is not present
          });
        }
      });
    }

    // Only search through users if we have no channel members found
    const [users, userTeams] = await Promise.all([
      data.length
        ? Promise.resolve(undefined)
        : this.usersService.list({
            includeExternalUsers: !this.replyingToThreadId && !!this.conversation.customerId,
            externalUsersCustomerId: this.conversation.customerId ?? undefined,
            internalUsersSlackOrgId: slackTeam?.slackEnterpriseId ?? slackTeamId ?? undefined,
            where: usersWhere,
            search: searchText,
            order: ['name'],
            descending: false,
            limit: 20,
          }),
      includeTeams
        ? this.slackUserGroupsService.list({
            limit: 20,
            order: ['name'],
            descending: false,
            search: searchText,
            where: userTeamsWhere,
          })
        : Promise.resolve(undefined),
    ]);

    if (requestId !== this.lastRequestId) {
      return;
    }

    if (userTeams?.data) {
      userTeams?.data?.forEach((team) => {
        data.push({
          id: team.id,
          name: `${team.name} (Team)`,
          image: '',
          slackId: team.slackId,
          type: SUPPORT_STEP_ASSIGNEE_TYPES.TEAM,
          object: team,
        });
      });
    }

    if (users?.data) {
      users?.data?.forEach((user) => {
        data.push({
          name: formatUserName(user, this.connectedTeams),
          id: user.id,
          image: user.photo,
          email: user.email,
          type: SUPPORT_STEP_ASSIGNEE_TYPES.USER,
          object: user,
          slackId: user.slackId || (user as any).slackIds?.[0], // Try slackIds array if slackId is not present
        });
      });
    }

    this.taggableUsers = data;

    if (this.taggableUsers.length) {
      this.highlightedItemIndex = 0;
    }
  }

  async searchChannels(searchText: string) {
    this.autocompleteSearchMode = 'channels';

    try {
      const { channels } = await this.slackService.getConnectedChannels(true);

      // Filter channels based on search text
      const filteredChannels = channels.filter(
        (channel) =>
          !searchText ||
          channel.name.toLowerCase().includes(searchText.toLowerCase()) ||
          channel.displayName?.toLowerCase().includes(searchText.toLowerCase()),
      );

      // Convert to format expected by the UI
      this.taggableChannels = filteredChannels.map((channel) => ({
        id: channel.id,
        name: channel.name,
        displayName: channel.displayName || channel.name,
        isPrivate: channel.isPrivate,
        slackId: channel.id, // Use channel.id as slackId for mentions
      }));

      if (this.taggableChannels.length) {
        this.highlightedItemIndex = 0;
        this.tippyService.show(this.tippyName);
        this.tippyService.getInstance(this.tippyName).setProps({
          getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
        });
      } else {
        this.tippyService.hide(this.tippyName);
      }
    } catch (error) {
      console.error('Error searching channels:', error);
      this.taggableChannels = [];
      this.tippyService.hide(this.tippyName);
    }
  }

  /**
   * Clears the channel members cache. Useful when channel membership might have changed.
   */
  clearChannelMembersCache() {
    this.channelMembersCache = null;
  }

  async selectUserByEmail(email: string, source: 'bcc' | 'cc') {
    if (!email) {
      return;
    }

    const entry: Recipient = { email, editable: true };
    if (source === 'cc') {
      this.ccs = _.uniqBy([...this.ccs, entry], 'email');
      this.ccText = '';
    } else if (source === 'bcc') {
      this.bccs = _.uniqBy([...this.bccs, entry], 'email');
      this.bccText = '';
    }
    this.hideAutocompleteTippy();
  }

  async selectTaggedUser(user: any) {
    // Check if slackId might be in a nested object
    const possibleSlackId =
      user.slackId || user.object?.slackId || user.object?.slackUser?.slackId || user.object?.slackUser?.id;

    // This may have come from the editor, the cc, or the bcc fields
    if (this.isCcFocused) {
      this.selectUserByEmail(user.email, 'cc');
    } else if (this.isBccFocused) {
      this.selectUserByEmail(user.email, 'bcc');
    } else {
      const displayLabel = user.name || user.email || 'Unknown User';

      // Determine the correct data-type and prefix based on user type
      let dataType = 'user-mention';
      let prefix = '@';
      if (user.type === SUPPORT_STEP_ASSIGNEE_TYPES.TEAM) {
        dataType = 'slack-usergroup-mention';
      } else if (user.type === 'channel') {
        dataType = 'slack-channel-mention';
        prefix = '#';
      }

      // Build attributes array for the mention span
      const attrs = [];
      attrs.push(`data-type="${dataType}"`);
      attrs.push(`class="mention-tag"`);

      // Add data-slack-id if available (required for backend parsing)
      if (possibleSlackId) {
        attrs.push(`data-slack-id="${possibleSlackId}"`);
      }

      // Add data-id for user mentions only (not for teams/channels)
      if (user.id && user.type === SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
        attrs.push(`data-id="${user.id}"`);
      }

      const token = `<span ${attrs.join(' ')}>${prefix}${displayLabel}</span> `;

      this.editor.commands.deleteRange(this.taggableRange);

      this.editor.commands.insertContent(token, {
        updateSelection: true,
        parseOptions: {
          preserveWhitespace: 'full',
        },
      });

      this.autocompleteSearchMode = 'none';
    }
  }

  async selectTaggedChannel(channel: any) {
    const range = {
      from: this.keyCommandStartIndex,
      to: this.editor.state.selection.anchor,
    };
    this.editor.commands.deleteRange(range);
    const token = `<span data-type="slack-channel-mention" data-slack-id="${channel.slackId}" class="mention-tag">#${channel.name}</span> `;
    this.editor.commands.insertContent(token, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
    this.hideAutocompleteTippy();
  }

  async searchSlashCommands(searchText: string) {
    this.autocompleteSearchMode = 'slash';

    this.slashCommands = this.SLASH_COMMAND_OPTIONS.filter((c) => c.title.toLowerCase().includes(searchText));

    if (this.slashCommands.length) {
      this.highlightedItemIndex = 0;
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } else {
      this.tippyService.hide(this.tippyName);
    }
  }

  async selectSlashCommand(command: any) {
    if (!command) {
      return;
    }

    this.analyticsService.trackEvent('Conversation Details', `Slash Command: ${command.title}`);

    if (command.type === SLASH_COMMAND_TYPE.TEMPLATE) {
      const range = {
        from: this.keyCommandStartIndex,
        to: this.editor.state.selection.anchor,
      };
      this.editor.commands.deleteRange(range);
      this.hideAutocompleteTippy();
      this.insertTemplate();
    } else if (command.type === SLASH_COMMAND_TYPE.ARTICLE) {
      // If they had started typing out a search term for the command, clear that out and return to the slash
      const range = {
        from: this.keyCommandStartIndex + 1,
        to: this.editor.state.selection.anchor,
      };
      this.editor.commands.deleteRange(range);

      this.searchArticles('');
    } else if (command.type === SLASH_COMMAND_TYPE.AUTOMATION) {
      // If they had started typing out a search term for the command, clear that out and return to the slash
      const range = {
        from: this.keyCommandStartIndex + 1,
        to: this.editor.state.selection.anchor,
      };
      this.editor.commands.deleteRange(range);

      this.searchAutomations('');
    } else if (command.type === SLASH_COMMAND_TYPE.GIF) {
      this.hideAutocompleteTippy();
      this.insertGif();
    } else if (command.type === SLASH_COMMAND_TYPE.ATTACHMENT) {
      const range = {
        from: this.keyCommandStartIndex,
        to: this.editor.state.selection.anchor,
      };
      this.editor.commands.deleteRange(range);
      this.hideAutocompleteTippy();
      this.insertImage();
    } else if (command.type === SLASH_COMMAND_TYPE.AI_RESPONSE) {
      const range = {
        from: this.keyCommandStartIndex,
        to: this.editor.state.selection.anchor,
      };
      this.editor.commands.deleteRange(range);
      this.hideAutocompleteTippy();
      this.requestAiResponse();
    }
  }

  async requestAiResponse() {
    const generatingText = 'Generating...';
    this.isGeneratingAiResponse = true;

    this.editor.commands.insertContent(`<i>${generatingText}</i>`, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
    this.editor.setEditable(false);

    try {
      const result: RunQueryResult = await this.apiService.postPromise('/knowledge-base/query', {
        customerId: this.conversation.customerId || '',
        conversationId: this.conversation.id,
        userId: this.conversation?.submitterUserId,
        projectId: this.conversation?.projectId,
      });

      const markdownIt = new Markdown({
        breaks: true,
        linkify: true,
      });

      // Convert response from markdown to html using markdown-it
      const responseHtml = markdownIt.renderInline(result.response || 'Unable to generate an answer');

      const range = {
        from: this.keyCommandStartIndex,
        to: this.keyCommandStartIndex + generatingText.length,
      };
      this.editor.commands.deleteRange(range);

      this.editor.commands.insertContent(responseHtml, {
        updateSelection: true,
        parseOptions: {
          preserveWhitespace: 'full',
        },
      });

      this.isGeneratingAiResponse = false;
      this.editor.setEditable(true);
    } catch (e) {
      this.msgSrvc.showError(e);
      const range = {
        from: this.keyCommandStartIndex,
        to: this.keyCommandStartIndex + generatingText.length,
      };
      this.editor.commands.deleteRange(range);
      this.isGeneratingAiResponse = false;
      this.editor.setEditable(true);
    }
  }

  async searchArticles(searchText: string) {
    this.autocompleteSearchMode = 'articles';

    try {
      const knowledgeBaseArticlesResponse = await this.knowledgebaseArticlesService.list({
        limit: 10,
        order: ['title'],
        descending: false,
        where: [
          {
            field: 'title',
            operator: 'like',
            value: `%${searchText}%`,
          },
        ],
      });

      this.articles = knowledgeBaseArticlesResponse.data;

      if (this.articles.length) {
        this.highlightedItemIndex = 0;
      }
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } catch (err) {
      this.tippyService.hide(this.tippyName);
    }
  }

  async selectArticle(article: Partial<KnowledgeBaseArticle>) {
    const range = {
      from: this.keyCommandStartIndex,
      to: this.editor.state.selection.anchor,
    };
    this.editor.commands.deleteRange(range);
    this.editor.commands.insertContent(`<a href="${article.url}">🔗 ${article.title}</a> `, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });

    this.didInsertKnowledgebaseArticle.next(article);

    setTimeout(() => {
      this.hideAutocompleteTippy();
    }, 400);
  }

  async searchAutomations(searchText: string) {
    this.autocompleteSearchMode = 'automations';

    try {
      const automations = await this.automationsService.list({
        limit: 10,
        order: ['name'],
        descending: false,
        where: [
          {
            field: 'trigger.type',
            operator: '==',
            value: 'manual',
          },
          {
            field: 'status',
            operator: '==',
            value: 'active',
          },
          {
            field: 'name',
            operator: 'like',
            value: `%${searchText}%`,
          },
        ],
      });

      this.automations = automations.data;

      if (this.automations.length) {
        this.highlightedItemIndex = 0;
      }
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } catch (err) {
      this.tippyService.hide(this.tippyName);
    }
  }

  async runAutomation(automation: Partial<Automation>) {
    const range = {
      from: this.keyCommandStartIndex,
      to: this.editor.state.selection.anchor,
    };
    this.editor.commands.deleteRange(range);

    const modal = await this.modalCtrl.create({
      component: TriggerManualAutomationModalComponent,
      componentProps: {
        automation,
        id: automation.id,
        conversationId: this.conversation.id,
      },
      cssClass: 'auto-height modal-sm',
    });
    modal.present();
    modal.onDidDismiss().then(async () => {
      this.didRunAutomation.next(automation);
    });

    setTimeout(() => {
      this.hideAutocompleteTippy();
    }, 400);
  }

  async searchEmojis(searchText: string) {
    if (!this.emojisMap) {
      this.emojisMap = await this.emojisService.getEmojiList();
    }

    this.autocompleteSearchMode = 'emojis';
    const limit = 12;

    const results = [];
    const list: any = Object.values(this.emojisMap);

    for (let entry of list) {
      let hasAddedEntry = false;
      for (let keyword of entry.keywords) {
        if (keyword.includes(searchText)) {
          results.push(entry);
          hasAddedEntry = true;
          break;
        }
      }

      if (
        !hasAddedEntry &&
        entry.skins[0]?.native &&
        results.length < limit &&
        (entry.id.toLowerCase().includes(searchText) || entry.name.toLowerCase().includes(searchText))
      ) {
        results.push(entry);
      }

      if (results.length >= limit) {
        break;
      }
    }

    this.emojisList = results;

    if (results.length) {
      this.highlightedItemIndex = 0;
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } else {
      this.tippyService.hide(this.tippyName);
    }
  }

  async selectEmoji(emoji: any) {
    const range = {
      from: this.keyCommandStartIndex,
      to: this.editor.state.selection.anchor,
    };
    this.editor.commands.deleteRange(range);
    this.editor.commands.insertContent(emoji.skins[0].native, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
    this.hideAutocompleteTippy();
  }

  async triage() {
    const modal = await this.modalCtrl.create({
      component: TriageComponent,
      cssClass: 'modal-content-only modal-sm',
      componentProps: {
        conversationId: this.conversation.id,
        shouldLoadDefaultTriageChannel: !this.getInternalTriageMessages()?.length,
        customerId: this.conversation.customerId,
        projectId: this.conversation.projectId,
      },
    });

    modal.present();
    // modal.onDidDismiss().then(async ({ data }: any) => {
    //   // No need to set any other parts of the UI – the conversation details component will set it for us
    //   if (data?.message) {
    //   }
    // });
  }

  async showReplyOptions() {
    const ADD_TRIAGE_LABEL = 'Create internal triage thread';
    const ADD_NOTE_WIDGET_EMAIL_LABEL = 'Add internal note';
    const options: ActionDropdownType[] = [];

    if (this.isEmailBased || this.isWidgetBased) {
      options.push({
        icon: 'create-outline',
        label: ADD_NOTE_WIDGET_EMAIL_LABEL,
      });
    } else if (!this.isDashboardBased) {
      if (!this.conversation?.triageThreads?.length) {
        options.push({
          icon: 'create-outline',
          label: ADD_TRIAGE_LABEL,
        });
      }
    }

    this.getInternalTriageMessages()
      ?.filter((triage: any) => triage.channelName)
      .forEach((triage: any) => {
        options.push({
          icon: 'create-outline',
          label: `Internal note in #${triage.channelName}`,
          metadata: {
            triage,
          },
        });
      });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        itemClass: 'ion-item-rounded-no-margin',
        options,
        callback: async (_idx: number, label: string, _id: string, metadata: any) => {
          popover.dismiss();

          if (label === ADD_TRIAGE_LABEL) {
            this.triage();
            this.analyticsService.trackEvent('Conversation Details', 'Manually Triage from Dropdown');
          } else if (label === ADD_NOTE_WIDGET_EMAIL_LABEL) {
            this.replyingToThreadId = this.conversation.initialMessage.ts;
            this.replyingToThreadLabel = undefined;
          } else if (metadata?.triage) {
            this.replyingToThreadId = metadata.triage.ts;
            this.replyingToThreadLabel = `in #${metadata.triage.channelName}`;
          }

          this.analyticsService.trackEvent('Conversation Details', 'Change Reply Type');
        },
      },
    });
    popover.present();
  }

  async getSnoozeDate() {
    const popover = await this.popoverCtrl.create({
      component: SnoozeComponent,
      event,
      showBackdrop: false,
    });

    popover.present();

    const { data } = await popover.onWillDismiss();

    return data?.snoozeDate;
  }

  selectReplyTab() {
    this.replyingToThreadId = undefined;
    this.replyingToThreadLabel = undefined;
  }

  async selectInternalNoteTab() {
    const triage = this.conversation.triageThreads?.filter(
      (triage: any) => triage.channelName && !triage.isCustomerFacing,
    )[0];

    if (this.isEmailBased || this.isWidgetBased || this.isBotDmBased) {
      this.replyingToThreadId = this.conversation.initialMessage.ts;
      this.replyingToThreadLabel = undefined;
    } else if (!triage) {
      this.analyticsService.trackEvent('Conversation Details', 'Manually Triage from Dropdown');
      this.triage();
    } else {
      this.replyingToThreadId = triage.ts;
      this.replyingToThreadLabel = `in #${triage.channelName ?? PRIVATE_CHANNEL_NAME}`;
    }
  }

  async showSendOptions() {
    const SEND_SNOOZE = 'Send and Snooze';
    const SEND_CLOSE = 'Send and Close';
    const options: ActionDropdownType[] = [
      {
        icon: 'alarm-outline',
        label: SEND_SNOOZE,
      },
      {
        icon: 'checkmark-outline',
        label: SEND_CLOSE,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        itemClass: 'ion-item-rounded-no-margin',
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === SEND_SNOOZE) {
            this.sendReply(true, false);
          } else if (label === SEND_CLOSE) {
            this.sendReply(false, true);
          }
        },
      },
    });
    popover.present();
  }

  async sendReply(snooze: boolean, close: boolean, isTest = false) {
    this.analyticsService.trackEvent(
      'Conversation Details',
      `Send Response${snooze ? ' and Snooze' : close ? 'and Close' : ''}`,
    );

    let snoozeDate: Date;

    if (snooze) {
      snoozeDate = await this.getSnoozeDate();

      if (!snoozeDate) {
        return;
      }
    }

    try {
      this.isSendingReply = true;

      const messageTextHtml = this.editor.getHTML();
      const messageBlocks = this.messageBlocks ?? [];

      const isStandardSlackChannel = !(
        this.isWidgetBased ||
        this.isEmailBased ||
        this.isMicrosoftTeamsBased ||
        this.isBotDmBased
      );
      const shouldSendExternally = !isStandardSlackChannel && !this.replyingToThreadId;

      if (shouldSendExternally) {
        let postfix = '';

        if (this.isEmailBased) {
          const ccsToAdd = this.ccs
            ?.filter((r) => !this.originalCcs.some((cc) => cc.email === r.email))
            .map((r) => r.email);

          if (ccsToAdd?.length) {
            postfix = `${postfix} ${UNTHREAD_SEND_EXTERNAL_CC} ${ccsToAdd.join(',').toLowerCase()}`;
          }

          if (this.bccs?.length) {
            postfix = `${postfix} ${UNTHREAD_SEND_EXTERNAL_BCC} ${this.bccs
              .map((r) => r.email)
              .join(',')
              .toLowerCase()}`;
          }

          // Find the original cc emails that are not in the current ccs array to determine which ones we need to remove
          const ccsToRemove = this.originalCcs
            ?.filter((r) => !this.ccs.some((cc) => cc.email === r.email))
            .map((r) => r.email);

          if (ccsToRemove?.length) {
            postfix = `${postfix} ${UNTHREAD_SEND_EXTERNAL_CC_REMOVE} ${ccsToRemove.join(',').toLowerCase()}`;
          }
        }

        if (this.authService.tenant.defaultToPublicMessages) {
          if (postfix.length) {
            postfix = [UNTHREAD_SEND_EXTERNAL_PREFIX, postfix].join(' ');
          }
        } else {
          postfix = [UNTHREAD_SEND_EXTERNAL_PREFIX, postfix, UNTHREAD_SEND_EXTERNAL_SEND].filter(Boolean).join(' ');
        }

        if (postfix.length) {
          messageBlocks.push({
            type: 'section',
            block_id: 'unthread-send-external',
            text: {
              type: 'mrkdwn',
              text: postfix,
            },
          });
        }
      }

      const triageThreadId =
        (this.isWidgetBased || this.isEmailBased || this.isMicrosoftTeamsBased || this.isBotDmBased) &&
        this.replyingToThreadId === this.conversation.initialMessage.ts
          ? null
          : this.replyingToThreadId;

      const { ts } = await this.conversationsService.createMessage(
        this.conversation.id,
        messageTextHtml,
        messageBlocks,
        triageThreadId,
        removeUndefinedFields({
          status: close ? CONVERSATION_STATUS.CLOSED : snoozeDate ? CONVERSATION_STATUS.ON_HOLD : undefined,
          wakeUpAt: snoozeDate ?? undefined,
        }),
        this.attachments,
        isStandardSlackChannel ? undefined : !shouldSendExternally,
        isTest,
      );

      this.didSendPendingMessage.next({
        content: messageTextHtml,
        isPrivateExplanation: !!this.replyingToThreadId,
        addSendingMessageId: shouldSendExternally && ts,
        ts,
        attachments: this.attachments,
      });

      if (!isTest) {
        this.editor.commands.clearContent();
        this.ccs = [];
        this.ccText = '';
        this.bccs = [];
        this.bccText = '';
        this.isContentEmpty = true;
        this.messageBlocks = undefined;
        this.templateHtml = undefined;
        this.attachments = [];
        delete this.conversationsService.draftsCache[this.conversation.id];
      }
    } catch (e) {
      this.msgSrvc.showError(e);
    } finally {
      this.isSendingReply = false;
    }
  }

  removeAttachment(index: number) {
    this.attachments.splice(index, 1);
  }

  async explainThreading() {
    const alert = await this.alertCtrl.create({
      subHeader: 'Your reply will start a thread in Slack, and all future replies will go to that thread',
    });

    await alert.present();
  }

  setEmailRecipients({ tos, ccs, bccs }: { tos?: Recipient[]; ccs?: Recipient[]; bccs?: Recipient[] }) {
    if (tos) {
      this.tos = tos.map((to) => ({
        email: to.email,
        name: to.name?.trim() || to.email,
        editable: false,
      }));
    }

    if (ccs) {
      this.ccs = ccs.map((cc) => ({
        email: cc.email,
        name: cc.name?.trim() || cc.email,
        editable: true,
      }));
      this.originalCcs = [...ccs];
    }

    if (bccs) {
      this.bccs = bccs.map((bcc) => ({
        email: bcc.email,
        name: bcc.name?.trim() || bcc.email,
        editable: true,
      }));
    }
  }

  changeTo() {
    const updateSubmitter = async (id: string, submitterUser: any) => {
      if (!submitterUser.email) {
        this.msgSrvc.show('No email found for user');
        return;
      }

      const loader = await this.loadingCtrl.create({
        message: 'Updating...',
        spinner: 'crescent',
      });
      loader.present();

      try {
        await this.conversationsService.update(this.conversation.id, {
          submitterUserId: id,
        });

        this.setEmailRecipients({
          tos: [
            {
              name: submitterUser.name || submitterUser.email,
              email: submitterUser.email,
            },
          ],
        });

        loader.dismiss();
      } catch (e) {
        loader.dismiss();
        this.msgSrvc.show('Failed to update, please try again');
        console.error(e);
      }
    };

    this.searchablePopoverService.allUsers({
      event,
      dynamicCreationCallback: async (text: string) => {
        const submitterUser = await this.usersService.create(text);
        updateSubmitter(submitterUser.id, submitterUser);
      },
      callback: async (id: string, name: string, submitterUser: any) => {
        updateSubmitter(id, submitterUser);
      },
    });
  }

  emitSaveEdit(): void {
    if (this.editor) {
      this.saveEditClicked.emit(this.editor.getHTML());
    }
  }

  emitCancelEdit(): void {
    this.cancelEditClicked.emit();
  }

  /**
   * Converts resolvedContent array to HTML with proper mention spans
   */
  private convertResolvedContentToHTML(resolvedContent: any[]): string {
    let html = '';

    for (const part of resolvedContent) {
      if (typeof part === 'string') {
        // TipTap will handle escaping when parsing
        html += part;
      } else if (typeof part === 'object' && part.type === 'user') {
        const userId = (part.user?.id || part.id || '').replace(/"/g, '&quot;');
        const slackId = (part.user?.slackId || part.user_id || '').replace(/"/g, '&quot;');
        const userName = part.user?.name || part.name || slackId || 'Unknown User';

        // Create HTML that matches TipTap Mention extension's parseHTML expectations
        html += `<span data-type="user-mention" data-id="${userId}" data-slack-id="${slackId}" class="mention-tag">@${userName}</span>`;
      } else if (typeof part === 'object' && part.type === 'usergroup') {
        const groupId = (part.id || '').replace(/"/g, '&quot;');
        const slackId = (part.usergroup_id || '').replace(/"/g, '&quot;');
        const groupName = part.name || slackId || 'Unknown Group';

        // Create HTML for user group mentions matching parseHTML expectations
        html += `<span data-type="slack-usergroup-mention" data-id="${groupId}" data-slack-id="${slackId}" class="mention-tag">@${groupName}</span>`;
      } else if (typeof part === 'object' && part.type === 'channel') {
        const channelId = (part.id || '').replace(/"/g, '&quot;');
        const channelName = part.name || part.id || 'unknown-channel';

        // Create HTML for channel mentions with proper styling
        html += `<span class="mention-tag">#${channelName}</span>`;
      } else if (typeof part === 'object') {
        // For unknown object types, show a meaningful fallback instead of JSON
        const displayName = part.name || part.id || '[Unknown]';
        html += displayName;
      }
    }

    return html;
  }

  shouldShowSlackAuthorDisclaimer(): boolean {
    // Show disclaimer when message will appear in Slack with user's identity

    // Dashboard messages don't go to Slack at all
    if (this.isDashboardBased) {
      return false;
    }

    // Internal notes stay in internal threads, not customer-facing Slack
    if (this.replyingToThreadId) {
      return false;
    }

    // Inline edits are modifications, not new messages
    if (this.isInlineEdit) {
      return false;
    }

    // Only show disclaimer for messages that actually go TO Slack
    // Standard Slack channels: Always go to Slack
    if (!(this.isWidgetBased || this.isEmailBased || this.isMicrosoftTeamsBased || this.isBotDmBased)) {
      return true;
    }

    // Bot DMs: Also go to Slack (with user attribution)
    if (this.isBotDmBased) {
      return true;
    }

    // Email/Widget/Teams: Go to customer, not Slack - no disclaimer needed
    return false;
  }

  getSlackAuthorDisclaimerText(): string {
    if (!this.shouldShowSlackAuthorDisclaimer()) {
      return '';
    }

    // Simplified message: if showing disclaimer, message will appear in Slack with user identity
    if (this.hasUserChatWriteScope) {
      return 'Your message will be posted as you';
    } else {
      return 'Message will be posted with your name and image';
    }
  }

  get sendButtonTooltipContent(): string {
    const baseTooltip = 'Send reply<br/><small>⌘ + Enter</small>';
    const disclaimerText = this.getSlackAuthorDisclaimerText();

    if (disclaimerText) {
      return `${baseTooltip}<br/><small style="color: #999; margin-top: 4px;">${disclaimerText}</small>`;
    }

    return baseTooltip;
  }

  get sendAndSnoozeTooltipContent(): string {
    const baseTooltip = 'Send and snooze';
    const disclaimerText = this.getSlackAuthorDisclaimerText();

    if (disclaimerText) {
      return `${baseTooltip}<br/><small style="color: #999; margin-top: 4px;">${disclaimerText}</small>`;
    }

    return baseTooltip;
  }

  get sendAndCloseTooltipContent(): string {
    const baseTooltip = 'Send and close<br/><small>⌘ + Shift + Enter</small>';
    const disclaimerText = this.getSlackAuthorDisclaimerText();

    if (disclaimerText) {
      return `${baseTooltip}<br/><small style="color: #999; margin-top: 4px;">${disclaimerText}</small>`;
    }

    return baseTooltip;
  }
}
